import React, { Fragment, useState, useRef, useEffect } from "react";
import { formatCurrency } from "../../utils/helpers";
import {
  closedAccountTooltip,
  formatInvoiceDateShort,
  formatMonthShortYm,
  invoiceRowKey,
  isInvoiceRowEditing,
  invoiceTableRowGroups,
} from "../../utils/invoiceMonthDisplay";
import { ArrowDownIcon, ArrowUpIcon, DownloadIcon, EditIcon, TrashIcon } from "../icons";
import InvoiceRowEditPanel from "./InvoiceRowEditPanel";
import InvoiceStatusCell from "./InvoiceStatusCell";
import { formatGlAllocationsLabel } from "../../utils/glCodes";
import { createPortal } from "react-dom";

// Total column count for the all-time table when the Credit column is shown.
// One fewer when "Display Credits" is unchecked.
// Columns: check, provider/account, month, mrc, total, inv, [credit], note, send, actions
const ALL_TIME_COL_COUNT = 12;

const baseTh =
  "px-1.5 py-2 text-left text-[12px] font-semibold uppercase tracking-[0.04em] text-[var(--ink,#1c1c1a)] bg-[var(--sunken2,#fbfaf8)]";
const sortableTh = `${baseTh} cursor-pointer select-none hover:text-black`;

const SortArrows = ({ sortConfig, sortKey }) => {
  const isActive = sortConfig.key === sortKey;
  return (
    <span className="inline-flex flex-col ml-1 -space-y-1 text-[var(--faint,#9a9a95)] shrink-0">
      <ArrowUpIcon className={`w-2.5 h-2.5 ${isActive && sortConfig.direction === "asc" ? "text-[var(--accent,#a51c1c)]" : ""}`} />
      <ArrowDownIcon className={`w-2.5 h-2.5 ${isActive && sortConfig.direction === "desc" ? "text-[var(--accent,#a51c1c)]" : ""}`} />
    </span>
  );
};

const SortableTh = ({ children, sortKey, sortConfig, onSort, className = "" }) => {
  const isActive = sortConfig.key === sortKey;
  return (
  <th onClick={() => onSort(sortKey)} className={`${sortableTh} ${isActive ? "text-[var(--accent,#a51c1c)]" : ""} ${className}`}>
    <span
      className={`inline-flex items-center ${
        className.includes("text-center") ? "justify-center w-full" : ""
      }`}
    >
      {children}
      <SortArrows sortConfig={sortConfig} sortKey={sortKey} />
    </span>
  </th>
  );
};

const ExternalSendIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
    <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
  </svg>
);

const SparkleNoteIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M9.813 2.659a1.5 1.5 0 012.374 0l1.22 1.84a1 1 0 00.757.424l2.2.255a1.5 1.5 0 01.831 2.548l-1.59 1.55a1 1 0 00-.287.884l.375 2.186a1.5 1.5 0 01-2.175 1.58l-1.967-.965a1 1 0 00-.878 0l-1.967.965a1.5 1.5 0 01-2.175-1.58l.375-2.186a1 1 0 00-.287-.884l-1.59-1.55a1.5 1.5 0 01.831-2.548l2.2-.255a1 1 0 00.757-.424l1.22-1.84z" />
  </svg>
);

const PlusIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const BanIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 11-12.728 12.728A9 9 0 0118.364 5.636z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.343 6.343l11.314 11.314" />
  </svg>
);

const CircuitIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h4m4 0h8M4 12h8m4 0h4M4 17h2m4 0h10" />
    <circle cx="10" cy="7" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="14" cy="12" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="8" cy="17" r="1.5" fill="currentColor" stroke="none" />
  </svg>
);

const DotsIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
    <circle cx="4" cy="10" r="1.5" />
    <circle cx="10" cy="10" r="1.5" />
    <circle cx="16" cy="10" r="1.5" />
  </svg>
);

// Same ActionMenu as the comparison table (copy verbatim, or extract to a shared file)
function ActionMenu({ items }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const ref = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (
        ref.current && !ref.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const reposition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200;
      setCoords({ top: openUp ? rect.top : rect.bottom, left: rect.right, openUp });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < 200;
      setCoords({ top: openUp ? rect.top : rect.bottom, left: rect.right, openUp });
    }
    setOpen((p) => !p);
  };

  if (items.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={handleOpen}
        className={`
          inline-flex items-center justify-center w-8 h-8 rounded-lg
          border border-stone-200 bg-white
          text-stone-400 transition-all duration-150
          hover:text-stone-700 hover:bg-stone-50 hover:border-stone-300
          focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400
          ${open ? "bg-stone-100 text-stone-700 border-stone-300" : ""}
        `}
      >
        <DotsIcon />
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            top: coords.openUp ? undefined : coords.top + 6,
            bottom: coords.openUp ? window.innerHeight - coords.top + 6 : undefined,
            left: coords.left - 192,
            zIndex: 9999,
          }}
          className="w-48 rounded-xl border border-stone-200/80 bg-white shadow-lg shadow-stone-200/60 ring-1 ring-black/[0.04] py-1 overflow-hidden"
        >
          {items.map((item, i) => (
            <button
              key={i}
              type="button"
              disabled={item.disabled}
              onClick={() => { if (!item.disabled) { item.onClick(); setOpen(false); } }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-left transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed ${item.danger ? "text-rose-600 hover:bg-rose-50 hover:text-rose-700" : "text-stone-700 hover:bg-stone-50 hover:text-stone-900"}`}
            >
              {item.icon && (
                <span className={`flex-shrink-0 ${item.danger ? "text-rose-500" : "text-stone-400"}`}>
                  {item.icon}
                </span>
              )}
              <span>{item.label}</span>
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

const GlCodeCell = ({ glCode, allocations }) => {
  const value = formatGlAllocationsLabel(allocations, glCode);
  if (!value) {
    return (
      <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-xs font-medium text-rose-600">
        No GL
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-md bg-stone-100 px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-stone-700 ring-1 ring-inset ring-stone-200"
      title="GL code"
    >
      {value}
    </span>
  );
};

const InvoicesAllTimeTable = ({
  rows,
  sortConfig,
  onSort,
  editingInvoiceId,
  editInvoiceDate,
  setEditInvoiceDate,
  editInvoiceNumber,
  setEditInvoiceNumber,
  editGlCode,
  setEditGlCode,
  editGlAllocations = [],
  setEditGlAllocations,
  editGlSplit = false,
  setEditGlSplit,
  glCodes = [],
  editMonthlyMrc,
  setEditMonthlyMrc,
  editTotalAmount,
  setEditTotalAmount,
  editCredit,
  setEditCredit,
  editTaxesAndSurcharges,
  setEditTaxesAndSurcharges,
  editDueDate,
  setEditDueDate,
  editNote,
  setEditNote,
  savingInvoiceId,
  generatingNoteId,
  renderNoteCell,
  canEditInvoice,
  canDeleteInvoice,
  canCloseAccount,
  onDownload,
  downloadingId,
  deletingId,
  onSaveEdit,
  onCancelEdit,
  onGenerateNote,
  canShowGenerateNote,
  onMarkReviewed,
  canShowMarkReviewed,
  onOpenDelete,
  onOpenClose,
  beginEditInvoice,
  canSendEmail,
  selectedEmailIds,
  onToggleEmailSelect,
  onSendEmail,
  onResendEmail,
  sendingEmailId,
  formatEmailSentDate,
  onToggleSelectAll,
  tableTotals,
  // "Display Credits" toggle — hidden by default to save space, since credits
  // are usually reviewed via the Credit status filter instead.
  showCreditColumn = false,
  showInvoiceNumberColumn = false,
  noteAutomationSettings,
  onViewCircuits,
}) => {
  const showCredit = showCreditColumn;
  const showInvoiceNumber = showInvoiceNumberColumn;
  const colSpanCount = ALL_TIME_COL_COUNT - (showCredit ? 0 : 1) - (showInvoiceNumber ? 0 : 1);
  const closeAccountItems = (inv) =>
    canCloseAccount
      ? [
          {
            label: "Close account",
            icon: <BanIcon className="w-4 h-4" />,
            onClick: () => onOpenClose?.(inv),
          },
        ]
      : [];
  const viewCircuitsItems = (inv) => {
    const account = String(inv?.billing_account || "").trim();
    if (!account || typeof onViewCircuits !== "function") return [];
    return [
      {
        label: "View circuit on this account",
        icon: <CircuitIcon className="w-4 h-4" />,
        onClick: () => onViewCircuits(account),
      },
    ];
  };
  const selectableOnPage = rows.filter((r) => r.id != null);
  const allPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((r) => selectedEmailIds?.has(r.id));

  return (
    <table className="w-full table-fixed border-collapse text-sm">
      <colgroup>
        <col style={{ width: "3%" }} />
        <col style={{ width: showCredit ? "16%" : "18%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: "12%" }} />
        <col style={{ width: "8%" }} />
        <col style={{ width: showCredit ? "10%" : "11%" }} />
        <col style={{ width: showCredit ? "9%" : "11%" }} />
        {showInvoiceNumber && <col style={{ width: showCredit ? "9%" : "11%" }} />}
        {showCredit && <col style={{ width: "8%" }} />}
        <col style={{ width: "4%" }} />
        <col style={{ width: showCredit ? "14%" : "15%" }} />
        <col style={{ width: "8%" }} />
      </colgroup>
      <thead className="sticky top-0 z-20 bg-white">
        <tr className="border-b border-stone-200">
          <th className={`${baseTh} pl-2`}>
            {canSendEmail && (
              <input
                type="checkbox"
                checked={allPageSelected}
                onChange={() => onToggleSelectAll?.(selectableOnPage.map((r) => r.id))}
                disabled={sendingEmailId != null || selectableOnPage.length === 0}
                className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                title="Select all on this page"
              />
            )}
          </th>
          <SortableTh sortKey="provider" sortConfig={sortConfig} onSort={onSort} className="whitespace-nowrap">
            Account
          </SortableTh>
          <SortableTh sortKey="gl_code" sortConfig={sortConfig} onSort={onSort} className="whitespace-nowrap">
            GL
          </SortableTh>
          <SortableTh sortKey="row_kind" sortConfig={sortConfig} onSort={onSort} className="whitespace-nowrap">
            Status
          </SortableTh>
          <SortableTh sortKey="invoice_date" sortConfig={sortConfig} onSort={onSort} className="text-center">Month</SortableTh>
          <SortableTh sortKey="monthly_mrc" sortConfig={sortConfig} onSort={onSort} className="text-right">MRC</SortableTh>
          <SortableTh sortKey="total_invoice_amount" sortConfig={sortConfig} onSort={onSort} className="text-center">
            <span className="leading-tight">
              Total
              <br />
              Amount
            </span>
          </SortableTh>
          {showInvoiceNumber && (
            <SortableTh sortKey="invoice_number" sortConfig={sortConfig} onSort={onSort} className="text-center">
            <span className="leading-tight">Invoice<br />No.</span>
            </SortableTh>
          )}
          {showCredit && (
            <SortableTh sortKey="credit_amount" sortConfig={sortConfig} onSort={onSort} className="text-center">Credit</SortableTh>
          )}
          <SortableTh sortKey="note" sortConfig={sortConfig} onSort={onSort} className="text-center">Note</SortableTh>
          <SortableTh
            sortKey="email_sent_at"
            sortConfig={sortConfig}
            onSort={onSort}
            className="text-center leading-tight"
          >
            <span title="Send to AP">
              Send
              <br />
              to AP
            </span>
          </SortableTh>
          <th className={`${baseTh} text-right pr-2 whitespace-nowrap`}>Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-stone-100">
        {invoiceTableRowGroups(rows, sortConfig?.key).map((group) => (
          <Fragment key={`provider-${group.provider ?? "sorted"}`}>
            {group.provider != null && (
            <tr className="bg-[var(--sunken2,#fbfaf8)]">
              <td className="pl-2 py-2" />
              <td
                colSpan={Math.max(1, colSpanCount - 1)}
                className="px-1.5 py-2 align-middle"
              >
                <span className="font-semibold text-[var(--ink,#1c1c1a)]">{group.provider}</span>
                <span className="ml-1.5 text-[12px] text-[var(--muted,#6b6a66)]">
                  {group.accountCount} {group.accountCount === 1 ? "account" : "accounts"} · {group.receivedCount} received
                </span>
              </td>
            </tr>
            )}
        {group.items.map((inv) => {
          const isEditing = isInvoiceRowEditing(editingInvoiceId, inv);
          const sentLabel = formatEmailSentDate?.(inv.email_sent_at);
          const canSelect = inv.id != null && !isEditing;
          const isSending = sendingEmailId === inv.id;
          const glForDisplay = isEditing
            ? formatGlAllocationsLabel(editGlAllocations, editGlCode)
            : formatGlAllocationsLabel(inv.gl_allocations, inv.gl_code);
          const isAccountClosed = inv.account_is_active === false;
          const closedTooltip = closedAccountTooltip(inv);

          return (
            <Fragment key={invoiceRowKey(inv)}>
              <tr
                className={isEditing ? "ring-2 ring-inset ring-sky-400" : "hover:bg-stone-50/80"}
                title={closedTooltip || undefined}
              >
                <td className="pl-2 py-2.5 align-middle">
                  {canSelect && canSendEmail ? (
                    <input
                      type="checkbox"
                      checked={selectedEmailIds?.has(inv.id)}
                      onChange={() => onToggleEmailSelect?.(inv.id)}
                      disabled={sendingEmailId != null}
                      className="rounded border-stone-300 text-stone-800 focus:ring-stone-500"
                    />
                  ) : (
                    <span className="inline-block w-4" aria-hidden="true" />
                  )}
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <div className="tabular-nums break-all leading-snug text-[var(--ink,#1c1c1a)] min-w-0">
                    {group.provider == null && inv.provider ? (
                      <div className="text-[11px] text-[var(--muted,#6b6a66)]">{inv.provider}</div>
                    ) : null}
                    <div className="inline-flex items-center gap-1.5 min-w-0">
                    <span className="min-w-0 break-all">{inv.billing_account}</span>
                    {isAccountClosed && (
                      <span
                        className="inline-flex shrink-0 text-red-600"
                        title={closedTooltip || "Closed account"}
                        aria-label={closedTooltip || "Closed account"}
                      >
                        <BanIcon className="w-3.5 h-3.5" />
                      </span>
                    )}
                    </div>
                  </div>
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <GlCodeCell glCode={glForDisplay} />
                </td>
                <td className="px-1.5 py-2.5 overflow-hidden align-middle">
                  <InvoiceStatusCell inv={inv} noteSettings={noteAutomationSettings} />
                </td>
                <td className="px-1.5 py-2.5 text-stone-600 text-center whitespace-nowrap overflow-hidden align-top">
                  {formatMonthShortYm(String(inv.invoice_date).slice(0, 7))}
                </td>
                <td className="px-1.5 py-2.5 text-right overflow-hidden align-top">
                  <div className="leading-snug">
                    <div className="font-semibold text-stone-900 tabular-nums">
                      {formatCurrency(
                        isEditing && editMonthlyMrc !== ""
                          ? Number(editMonthlyMrc) || 0
                          : inv.monthly_mrc ?? 0,
                      )}
                    </div>
                    <div className="text-stone-500">
                      {formatInvoiceDateShort(
                        isEditing && editInvoiceDate ? editInvoiceDate : inv.invoice_date,
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-1.5 py-2.5 text-center font-semibold text-stone-900 overflow-hidden tabular-nums align-top">
                  {formatCurrency(
                    isEditing && editTotalAmount !== "" ? Number(editTotalAmount) || 0 : inv.total_invoice_amount ?? 0,
                  )}
                </td>
                {showInvoiceNumber && (
                <td className="px-1.5 py-2.5 text-stone-600 text-center overflow-hidden break-all align-top">
                  {(() => {
                    const num = isEditing && editInvoiceNumber != null
                      ? String(editInvoiceNumber).trim()
                      : inv.invoice_number != null ? String(inv.invoice_number).trim() : "";
                    return num || <span className="text-stone-400">—</span>;
                  })()}
                </td>
                )}
                {showCredit && (
                <td className="px-1.5 py-2.5 text-center text-stone-600 overflow-hidden tabular-nums align-top">
                  {(() => {
                    const creditValue = isEditing && editCredit !== ""
                      ? Number(editCredit) || 0
                      : inv.credit_amount != null ? Number(inv.credit_amount) : null;
                    return creditValue != null && creditValue > 0
                      ? formatCurrency(creditValue)
                      : <span className="text-stone-400">—</span>;
                  })()}
                </td>
                )}
                <td className="px-1.5 py-2.5 align-top overflow-hidden text-center">
                  {isEditing ? <span className="text-stone-400 text-xs italic">—</span> : renderNoteCell(inv)}
                </td>
                <td className="px-1.5 py-2.5 align-top overflow-hidden text-center">
                  {canSelect && canSendEmail ? (
                    sentLabel ? (
                      <span className="inline-flex items-center justify-center gap-1 text-xs font-medium text-emerald-700">
                        <svg className="w-3.5 h-3.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {formatInvoiceDateShort(inv.email_sent_at)}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSendEmail?.(inv.id)}
                        disabled={isSending || sendingEmailId != null}
                        className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2 py-1 text-xs font-medium text-stone-800 hover:bg-stone-50 disabled:opacity-50"
                      >
                        {isSending ? "Sending…" : "Send"} <ExternalSendIcon />
                      </button>
                    )
                  ) : (
                    <span className="text-stone-400">—</span>
                  )}
                </td>
                <td className="px-1.5 py-2.5 pr-2 text-right whitespace-nowrap overflow-hidden align-top">
                  {!isEditing && (
                    <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => onDownload(inv.id)}
                      disabled={downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id}
                      title="Download"
                      aria-label="Download"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-sky-200 bg-sky-50 text-sky-600 transition-all duration-150 hover:bg-sky-100 hover:border-sky-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <DownloadIcon className="w-4 h-4" />
                    </button>
                    <ActionMenu
                      items={[
                        // {
                        //   label: "Download",
                        //   icon: <DownloadIcon className="w-4 h-4" />,
                        //   disabled: downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id,
                        //   onClick: () => onDownload(inv.id),
                        // },
                        ...(canEditInvoice ? [{
                          label: "Edit invoice",
                          icon: <EditIcon className="w-4 h-4" />,
                          disabled: deletingId === inv.id || downloadingId === inv.id || editingInvoiceId != null,
                          onClick: () => beginEditInvoice(inv),
                        }] : []),
                        ...(canShowMarkReviewed?.(inv)
                          ? [{
                              label: "Mark reviewed",
                              icon: <CheckIcon className="w-4 h-4" />,
                              disabled: deletingId === inv.id || downloadingId === inv.id || savingInvoiceId === inv.id,
                              onClick: () => onMarkReviewed?.(inv),
                            }]
                          : []),
                        ...(canSendEmail && sentLabel
                          ? [{
                              label: "Resend",
                              icon: <ExternalSendIcon className="w-4 h-4" />,
                              disabled: isSending || sendingEmailId != null,
                              onClick: () => onResendEmail?.(inv.id),
                            }]
                          : []),
                        ...(canDeleteInvoice ? [{
                          label: "Delete invoice",
                          icon: <TrashIcon className="w-4 h-4" />,
                          disabled: downloadingId === inv.id || deletingId === inv.id || savingInvoiceId === inv.id,
                          danger: true,
                          onClick: () => onOpenDelete(inv),
                        }] : []),
                        ...viewCircuitsItems(inv),
                        ...closeAccountItems(inv),
                      ]}
                    />
                    </div>
                  )}
                </td>
              </tr>
              {isEditing && (
                <InvoiceRowEditPanel
                  invoice={inv}
                  editInvoiceDate={editInvoiceDate}
                  setEditInvoiceDate={setEditInvoiceDate}
                  editInvoiceNumber={editInvoiceNumber}
                  setEditInvoiceNumber={setEditInvoiceNumber}
                  editGlCode={editGlCode}
                  setEditGlCode={setEditGlCode}
                  editGlAllocations={editGlAllocations}
                  setEditGlAllocations={setEditGlAllocations}
                  editGlSplit={editGlSplit}
                  setEditGlSplit={setEditGlSplit}
                  glCodes={glCodes}
                  editMonthlyMrc={editMonthlyMrc}
                  setEditMonthlyMrc={setEditMonthlyMrc}
                  editTotalAmount={editTotalAmount}
                  setEditTotalAmount={setEditTotalAmount}
                  editCredit={editCredit}
                  setEditCredit={setEditCredit}
                  editTaxesAndSurcharges={editTaxesAndSurcharges}
                  setEditTaxesAndSurcharges={setEditTaxesAndSurcharges}
                  editDueDate={editDueDate}
                  setEditDueDate={setEditDueDate}
                  editNote={editNote}
                  setEditNote={setEditNote}
                  saving={savingInvoiceId === inv.id}
                  generatingNote={generatingNoteId === inv.id}
                  onSave={() => onSaveEdit(inv.id)}
                  onCancel={onCancelEdit}
                  onGenerateNote={
                    canEditInvoice && canShowGenerateNote?.(inv) ? () => onGenerateNote(inv.id) : undefined
                  }
                />
              )}
            </Fragment>
          );
        })}
          </Fragment>
        ))}
      </tbody>
      {tableTotals && (
        <tfoot className="sticky bottom-0 z-10 border-t-2 border-stone-300 bg-stone-50">
          <tr>
            {/* One cell per column so totals stay under MRC / Total Amount / Credit */}
            <td className="pl-2 py-2.5" />
            <td className="px-1.5 py-2.5 text-xs font-semibold text-stone-900 overflow-hidden">
              Total ({tableTotals.count} {tableTotals.count === 1 ? "Record" : "Records"})
            </td>
            <td className="px-1.5 py-2.5" />
            <td className="px-1.5 py-2.5 text-right font-semibold text-stone-900 tabular-nums overflow-hidden">
              <span className="block text-xs leading-snug break-all">
                {formatCurrency(tableTotals.mrc)}
              </span>
            </td>
            <td className="px-1.5 py-2.5 text-center font-semibold text-stone-900 tabular-nums overflow-hidden">
              <span className="block text-xs leading-snug break-all">
                {formatCurrency(tableTotals.total)}
              </span>
            </td>
            {/* <td className="px-1.5 py-2.5" /> */}
            {showInvoiceNumber && <td className="px-1.5 py-2.5" />}
            {showCredit && (
              <td className="px-1.5 py-2.5 text-center font-semibold text-stone-700 tabular-nums overflow-hidden">
                <span className="block text-xs leading-snug break-all">
                  {tableTotals.credit > 0 ? formatCurrency(tableTotals.credit) : "—"}
                </span>
              </td>
            )}
            <td className="px-1.5 py-2.5" />
            <td className="px-1.5 py-2.5" />
            <td className="px-1.5 py-2.5 pr-2" />
          </tr>
        </tfoot>
      )}
    </table>
  );
};

export default InvoicesAllTimeTable;